import { useState, useContext } from "react";
import {
  Typography,
  Box,
  Grid,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  TableContainer,
  Chip,
  Switch,
  Stack,
} from "@mui/material";

// Import styles and context
import "../../../Style/pagestyle.scss";
import { AuthContext } from "../../../Context/AuthContext";
import { ButtonCreate, SearchTextField } from "../../../Utils/Component";
import EmptyData from "../../../Components/Include/EmptyData/EmptyData";
import LoadingPage from "../../../Utils/LoadingPage";
import FooterPagination from "../../../Utils/FooterPagination";
import useWithDimension from "../../../Hooks/useWithDimension";
import OptionPriceForm from "../../../Components/OptionPrice/OptionPriceForm";
import OptionPriceAction from "../../../Components/OptionPrice/OptionPriceAction";
import useOptionPriceForm from "../../../Components/OptionPrice/Hooks/useOptionPriceForm";
import useGetOptionPricePagination from "../../../Hooks/useGetOptionPricePagination";

export default function OptionPrice({ classId }) {
  const { t } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const handleOpenCreate = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { width } = useWithDimension();
  const {
    page,
    limit,
    setPage,
    refetch,
    loading,
    setLoading,
    tableData,
    paginationData,
    handleLimit,
    handleSearch,
  } = useGetOptionPricePagination({ classId });

  const { updateOptionPrice } = useOptionPriceForm({
    resetForm: () => {},
    setRefetch: refetch,
    handleClose: () => {},
  });

  const handleToggleDefault = (row, checked) => {
    const formatIds = (items) =>
      (items || []).map((item) =>
        typeof item === "object" && item !== null ? item._id : item,
      );

    updateOptionPrice({
      variables: {
        id: row._id,
        input: {
          accessedLessonIds: formatIds(row.accessedLessonIds),
          packagepriceIds: formatIds(row.packagepriceIds),
          payStatus: row.payStatus,
          default: checked,
        },
      },
    });
  };

  return (
    <div className="page-container">
      <Box sx={{ marginTop: "10px" }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={4} lg={3} xl={2}>
            <Typography className={t("class-name-text-field-title")}>{t("search")}</Typography>
            <SearchTextField
              handleSearch={handleSearch}
              className="text-field"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={8} lg={9} xl={10}>
            <ButtonCreate
              handleOpen={handleOpenCreate}
              classBtn="btn-create"
              classIcon="icon-add"
            />
            {open && (
              <OptionPriceForm
                open={open}
                setRefetch={refetch}
                dialogTitle="Create"
                handleClose={handleClose}
                classId={classId}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      <Box className="body-container">
        <TableContainer sx={{ maxWidth: `${width}px` }}>
          <Table className="table">
            <TableHead className="header-row">
              <TableRow>
                <TableCell
                  width="5%"
                  className={t("class-name-header-title-start")}
                >
                  {t("thead_no")}
                </TableCell>

                <TableCell width="20%" className={t("class-name-header-title")}>
                  {t("page_package_price")}
                </TableCell>

                <TableCell width="25%" className={t("class-name-header-title")}>
                  {t("thead_accessed_lesson")}
                </TableCell>

                <TableCell
                  width="20%"
                  className={t("class-name-header-title")}
                  align="center"
                >
                  {t("thead_pay_status")}
                </TableCell>

                <TableCell width="20%" className={t("class-name-header-title")}>
                  {t("thead_default")}
                </TableCell>

                <TableCell
                  width="10%"
                  align="right"
                  className={t("class-name-header-title-end")}
                />
              </TableRow>
            </TableHead>

            {loading ? (
              <TableBody className="body">
                <TableRow className="body-row">
                  <TableCell colSpan={12} className="body-cell" align="center">
                    <LoadingPage />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : !tableData || tableData.length === 0 ? (
              <TableBody className="body">
                <TableRow className="body-row">
                  <TableCell colSpan={12} className="body-cell" align="center">
                    <EmptyData />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody className="body">
                {tableData.map((row, index) => (
                  <TableRow className="body-row" key={row._id || index}>
                    <TableCell className="body-cell-start">
                      {(page - 1) * limit + index + 1}
                    </TableCell>

                    <TableCell className="body-cell">
                      {row.packagepriceIds[0]?.title} -{" "}
                      {row.packagepriceIds[0]?.groupFeeId.price} $
                      {/* <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {Array.isArray(row?.packagepriceIds) &&
                        row.packagepriceIds.length > 0 ? (
                          row.packagepriceIds.map((item) => (
                            <Typography key={item._id}>
                              {`${item.title} ${item.classId?.courseId?.enName || item.classId?.className || ""} - ${item.groupFeeId?.title + ": " || ""} 
                              ${`${item.groupFeeId.price}$`}`}
                            </Typography>
                          ))
                        ) : (
                          <span>-</span>
                        )}
                      </Stack> */}
                    </TableCell>

                    <TableCell className="body-cell">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {Array.isArray(row?.accessedLessonIds) &&
                        row.accessedLessonIds.length > 0 ? (
                          row.accessedLessonIds.map((item) => (
                            <Typography key={item._id}>
                              {`${item.title || ""}`}
                            </Typography>
                          ))
                        ) : (
                          <span>-</span>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell className="body-cell" align="center">
                      <Chip
                        size="small"
                        color={row?.payStatus === "pay" ? "primary" : "success"}
                        label={row?.payStatus || "-"}
                      />
                    </TableCell>

                    <TableCell className="body-cell">
                      <Switch
                        checked={Boolean(row?.default)}
                        onChange={(e) =>
                          handleToggleDefault(row, e.target.checked)
                        }
                      />
                    </TableCell>

                    <TableCell className="body-cell-end" align="right">
                      <OptionPriceAction
                        editData={row}
                        setRefetch={refetch}
                        classId={classId}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <FooterPagination
          totalPages={paginationData?.totalPages}
          totalDocs={paginationData?.totalDocs}
          limit={limit}
          page={page}
          setPage={setPage}
          handleLimit={handleLimit}
          setLoading={setLoading}
        />
      </Box>
    </div>
  );
}
